import { useState, type FormEvent } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api, type ApiErrorBody } from "../api.js";
import type { StaffSession } from "./session.js";

// S01 Sign in (S-007; SEC-06, TEN-04). Email and password. Refusals come from the api's rule
// message and never say which part was wrong; a user of a suspended tenant sees "Your account is
// not active. Please contact ZD support." Entered data is kept after an error, except that the
// password is cleared after a refusal so it is typed again. "Forgot your password?" opens the
// S-008 reset request (password/ForgotPassword.tsx).
// Functional and minimal: base styles only (parlour.css); restyled when the ZD shell lands.

const PASSWORD_MIN = 10;

export function SignIn({ onSignedIn, onForgot }: { onSignedIn: (s: StaffSession) => void; onForgot?: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [fields, setFields] = useState<Record<string, string>>({});

  function showError(err: ApiErrorBody | undefined, status: number) {
    setFields(err?.fields ?? {});
    setError(status === 0 ? t("common.errors.no_connection") : err?.code === "VALIDATION" ? undefined : (err?.message ?? t("common.errors.validation")));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // The same checks as the api, before sending, so a mistyped address is shown at once.
    const local: Record<string, string> = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) local.email = t("auth.signin.email_invalid");
    if (!password) local.password = t("auth.signin.password_required");
    else if (password.length < PASSWORD_MIN) local.password = t("parlour.signin.password_rule");
    if (Object.keys(local).length) {
      setError(undefined);
      setFields(local);
      return;
    }
    setBusy(true);
    const r = await api<StaffSession>("/v1/parlour/session", { method: "POST", body: { email: email.trim(), password } });
    setBusy(false);
    if (r.ok) {
      setPassword("");
      return onSignedIn(r.data);
    }
    showError(r.error, r.status);
    if (r.status !== 0) setPassword("");
  }

  return (
    <main className="s01">
      <h1>{t("parlour.shell.app_name")}</h1>
      <h2>{t("parlour.signin.title")}</h2>
      <p>{t("parlour.signin.intro")}</p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <form onSubmit={submit} noValidate aria-label={t("parlour.signin.title")}>
        <label>
          {t("parlour.signin.email_label")}
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!fields.email}
          />
        </label>
        {fields.email && <p className="field-error">{fields.email}</p>}
        <label>
          {t("parlour.signin.password_label")}
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            minLength={PASSWORD_MIN}
            maxLength={200}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={!!fields.password}
            aria-describedby="s01-password-hint"
          />
        </label>
        <p id="s01-password-hint" className="hint">
          {t("parlour.signin.password_hint")}
        </p>
        {fields.password && <p className="field-error">{fields.password}</p>}
        <button type="submit" disabled={busy}>
          {busy ? t("parlour.signin.working") : t("parlour.signin.submit")}
        </button>
      </form>
      {onForgot && (
        <p>
          <button type="button" onClick={onForgot}>
            {t("parlour.signin.forgot")}
          </button>
        </p>
      )}
    </main>
  );
}
